import React, { useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import AwardProgressBar from './AwardProgressBar';
import { useProfile } from '../../context/ProfileContext';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  Title, 
  Tooltip, 
  Legend 
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const SongDetailChart = ({ song, monthKeys }) => {
  const { isDarkMode } = useProfile();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Parse chart details and streams
  const chartInfo = useMemo(() => {
    if (!monthKeys || !song.stats) {
      return { labels: [], datasets: [], suggestedMax: 10 };
    }

    const firstDataIndex = monthKeys.findIndex(key => 
      (song.stats[`monthlyTotals.${key}`] || 0) > 0
    );

    let labels = [];
    if (firstDataIndex === -1) {
      labels = monthKeys.slice(-6);
    } else {
      const startIndex = Math.max(0, firstDataIndex - 1);
      labels = monthKeys.slice(startIndex);
      
      if (labels.length < 6) {
        const needed = 6 - labels.length;
        const lastMonthString = labels[labels.length - 1];
        const [month, year] = lastMonthString.split('-').map(Number);
        const lastDate = new Date(year, month - 1);
        for (let i = 0; i < needed; i++) {
          lastDate.setMonth(lastDate.getMonth() + 1);
          const nextMonth = (lastDate.getMonth() + 1).toString().padStart(2, '0');
          const nextYear = lastDate.getFullYear();
          labels.push(`${nextMonth}-${nextYear}`);
        }
      }
    }
    
    const leisureData = [];
    const discoveryData = [];

    labels.forEach((key) => {
      const totalStreams = song.stats[`monthlyTotals.${key}`] || 0;
      const discoveryStreams = song.stats[`monthlyTrendsTotals.${key}`] || 0;
      leisureData.push(totalStreams - discoveryStreams);
      discoveryData.push(discoveryStreams);
    });
    
    const maxValue = Math.max(0, ...leisureData, ...discoveryData);

    return {
      labels: labels,
      datasets: [
        {
          label: 'Discovery Streams',
          data: discoveryData,
          borderColor: 'rgb(249, 115, 22)',
          backgroundColor: 'rgba(249, 115, 22, 0.2)',
          tension: 0.2,
        },
        {
          label: 'Leisure Streams',
          data: leisureData,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.2)',
          tension: 0.2,
        },
      ],
      suggestedMax: maxValue < 10 ? 10 : maxValue + 5,
    };
  }, [song, monthKeys]);

  // Safely compute peak chart stats for the snapshot info box
  const careerStats = useMemo(() => {
    let peakPosition = song.lastPosition || null;
    if (song.chartHistory && typeof song.chartHistory === 'object') {
      const positions = Object.values(song.chartHistory).filter(pos => typeof pos === 'number');
      if (positions.length > 0) {
        peakPosition = Math.min(...positions);
      }
    }
    return {
      peakPosition,
      weeksOnChart: song.totalWeeksOnChart || 0,
      allTimeStreams: song.stats?.allTimeStreams || 0,
      downloads: song.stats?.totalDownloads || song.downloads || 0
    };
  }, [song]);

  const chartOptions = useMemo(() => {
    const gridColor = isDarkMode ? 'rgba(63, 63, 70, 0.4)' : 'rgba(228, 228, 231, 0.8)';
    const textColor = isDarkMode ? '#a1a1aa' : '#71717a';

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { 
          position: 'top', 
          labels: { 
            usePointStyle: true,
            color: textColor,
            font: { weight: 'bold', size: 11 }
          } 
        },
        title: { 
          display: true, 
          text: `Streams for "${song.title}"`, 
          color: isDarkMode ? '#f4f4f5' : '#18181b',
          font: { size: 16, weight: 'bold' } 
        },
      },
      scales: {
        x: {
          grid: { color: gridColor },
          ticks: { color: textColor }
        },
        y: {
          beginAtZero: true,
          suggestedMax: chartInfo.suggestedMax,
          grid: { color: gridColor },
          ticks: { 
            precision: 0,
            color: textColor
          }
        }
      }
    };
  }, [isDarkMode, chartInfo.suggestedMax, song.title]);
  
  const chartData = {
    labels: chartInfo.labels,
    datasets: chartInfo.datasets,
  };

  return (
    <>
      <div className={`p-4 my-3 rounded-2xl border transition-colors duration-500 ${
        isDarkMode ? "bg-zinc-900 border-zinc-850 text-white" : "bg-white border-gray-150 text-gray-900"
      }`}>
        
        {/* Header Action Row */}
        <div className="flex justify-between items-center mb-4">
          <span className="text-[10px] font-black uppercase tracking-widest opacity-40">Detailed Track Analysis</span>
          <button 
            onClick={() => setIsModalOpen(true)} 
            className={`p-1.5 rounded-lg border transition-all ${
              isDarkMode 
                ? "border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-800" 
                : "border-gray-100 text-gray-400 hover:text-gray-600 hover:bg-gray-50"
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </div>

        {/* Chart Frame */}
        {song.stats?.allTimeStreams > 0 ? (
          <div className="h-80 w-full">
            <Line options={chartOptions} data={chartData} />
          </div>
        ) : (
          <div className="text-center py-12">
            <p className={isDarkMode ? "text-zinc-500" : "text-gray-400"}>
              No stream data to display for "{song.title}".
            </p>
          </div>
        )}
        
        {/* Divider */}
        <div className={`mt-4 mb-3 border-t ${
          isDarkMode ? "border-zinc-800" : "border-gray-100"
        }`} />

        {/* Progress Tracker Card */}
        <AwardProgressBar song={song} />

        {/* ✅ NEW: STATIC DATA SNAPSHOT BOX */}
        <div className="grid grid-cols-2 gap-3 mt-3">
          
          {/* Streams & Downloads Box */}
          <div className={`p-3 rounded-xl border flex flex-col justify-center ${
            isDarkMode ? "bg-zinc-950/30 border-zinc-850/60" : "bg-gray-50 border-gray-100"
          }`}>
            <span className="text-[9px] font-bold uppercase tracking-wider opacity-40">Lifetime Volume</span>
            <p className="text-lg font-black text-brand-pink mt-1">
              {careerStats.allTimeStreams.toLocaleString()}
              <span className={`text-[10px] font-medium ml-1 ${isDarkMode ? "text-zinc-400" : "text-gray-500"}`}>Plays</span>
            </p>
            <p className={`text-[10px] font-semibold mt-0.5 opacity-60`}>
              {careerStats.downloads.toLocaleString()} downloads
            </p>
          </div>

          {/* Chart History Box */}
          <div className={`p-3 rounded-xl border flex flex-col justify-center ${
            isDarkMode ? "bg-zinc-950/30 border-zinc-850/60" : "bg-gray-50 border-gray-100"
          }`}>
            <span className="text-[9px] font-bold uppercase tracking-wider opacity-40">Chart History</span>
            <p className="text-lg font-black text-purple-400 mt-1">
              {careerStats.peakPosition ? `#${careerStats.peakPosition}` : 'Unranked'}
              <span className={`text-[10px] font-medium ml-1 ${isDarkMode ? "text-zinc-400" : "text-gray-500"}`}>Peak</span>
            </p>
            <p className={`text-[10px] font-semibold mt-0.5 opacity-60`}>
              {careerStats.weeksOnChart} {careerStats.weeksOnChart === 1 ? 'week' : 'weeks'} charted
            </p>
          </div>

        </div>

      </div>

      {/* Stream explanation modal */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black/60 flex justify-center items-center z-[150] p-4"
          onClick={() => setIsModalOpen(false)}
        >
          <div 
            className={`rounded-2xl shadow-2xl p-6 max-w-md w-full border ${
              isDarkMode ? "bg-zinc-900 border-zinc-800 text-zinc-100" : "bg-white border-gray-100 text-gray-800"
            }`}
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold mb-4">Stream Types Explained</h3>
            <div className="space-y-4 text-sm opacity-85 leading-relaxed">
              <p>
                <strong className="text-orange-500">Discovery Streams</strong> are plays from sources like the main 'Trends' feed. These streams help new listeners find your music.
              </p>
              <p>
                <strong className="text-blue-500">Leisure Streams</strong> are plays from your existing fans, such as when they play your music from search, your discography, an album, playlist, or their listening history.
              </p>
            </div>
            <button
              onClick={() => setIsModalOpen(false)}
              className="mt-6 w-full bg-brand-pink text-white font-bold py-3 px-4 rounded-xl"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default SongDetailChart;